import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { 
  Award, Users, Lightbulb, Settings, Share2, TrendingUp,
  CheckCircle, Star, Shield, Globe, ArrowRight, Sparkles,
  Building2, User, Mail, Phone, MapPin, Briefcase,
  Target, Zap, BookOpen, Headphones, DollarSign, Network,
  ChevronDown, ChevronUp, Check, X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Partner Type Icons mapping
const PARTNER_TYPE_ICONS = {
  credibility: Award,
  client_network: Users,
  advisor: Lightbulb,
  implementation: Settings,
  referral: Share2,
  channel: TrendingUp
};

// Tier Icons mapping
const TIER_ICONS = {
  registered: CheckCircle,
  authorized: Shield,
  strategic: Star
};

const PartnerProgramPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [partnerData, setPartnerData] = useState(null);
  const [selectedPartnerType, setSelectedPartnerType] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  
  const [formData, setFormData] = useState({
    applicant_type: 'organization',
    organization_name: '',
    organization_legal_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_title: '',
    country: '',
    city: '',
    timezone: '',
    website: '',
    linkedin_url: '',
    partner_type: '',
    industries_served: [],
    geographic_coverage: [],
    client_base_estimate: '',
    services_offered: [],
    prior_certifications: '',
    motivation: '',
    referral_source: '',
    terms_accepted: false,
    privacy_accepted: false,
    marketing_opt_in: false
  });

  useEffect(() => {
    fetchPartnerData();
  }, []);

  const fetchPartnerData = async () => {
    try {
      const response = await fetch(`${API}/partners/benefits`);
      const data = await response.json();
      setPartnerData(data);
    } catch (error) {
      console.error('Error fetching partner data:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(`${API}/partners/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setShowApplicationForm(false);
      } else {
        setSubmitError(result.detail || 'Failed to submit application');
      }
    } catch (error) {
      setSubmitError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const industries = [
    "Technology", "Healthcare", "Finance", "Manufacturing", "Retail",
    "Education", "Energy", "Hospitality", "Professional Services", "Non-Profit"
  ];

  const regions = [
    "North America", "Europe", "Asia Pacific", "Middle East", "Africa",
    "Latin America", "Global"
  ];

  const services = [
    "Consulting", "Implementation", "Training", "Advisory", "Audit",
    "Technology Solutions", "Marketing", "Sales"
  ];

  const faqs = [
    {
      question: "How long does the application process take?",
      answer: "Our team reviews applications within 2-3 business days. You'll receive an email notification once your application is processed."
    },
    {
      question: "What are the requirements to become a partner?",
      answer: "Requirements vary by partner type. Generally, we look for organizations or individuals with relevant expertise, a professional network, and alignment with CNESS values."
    },
    {
      question: "How does the commission structure work?",
      answer: "Commission rates depend on your partner type and tier. Referral partners earn per-lead fees, while Channel partners can earn up to 20% revenue share at Strategic tier."
    },
    {
      question: "Can I upgrade my partner tier?",
      answer: "Yes! Partners can progress from Registered to Authorized to Strategic by meeting performance criteria and completing required training."
    },
    {
      question: "What support do partners receive?",
      answer: "All partners get access to our partner portal, training resources, marketing materials, and dedicated support. Higher tiers receive additional benefits like dedicated partner managers."
    }
  ];

  // Hero Section
  const HeroSection = () => (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-24">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-40"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-white/90 text-sm font-medium">Join 500+ Partners Worldwide</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Grow Your Business with{' '}
            <span className="bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400 bg-clip-text text-transparent">
              CNESS
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed">
            Join a global ecosystem of conscious business leaders. 
            Expand your reach, grow revenue, and make an impact.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => setShowApplicationForm(true)}
              className="px-8 py-6 text-lg bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-semibold rounded-full shadow-2xl shadow-orange-500/30 transition-all duration-300 hover:scale-105"
              data-testid="become-partner-btn"
            >
              Become a Partner
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => setActiveSection('types')}
              className="px-8 py-6 text-lg border-2 border-white/30 text-white hover:bg-white/10 rounded-full backdrop-blur-sm"
              data-testid="explore-programs-btn"
            >
              Explore Programs
            </Button>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 pt-16 border-t border-white/10">
            {[
              { value: "500+", label: "Global Partners" },
              { value: "25+", label: "Countries" },
              { value: "94%", label: "Success Rate" },
              { value: "35%", label: "Avg. Revenue Growth" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  // Why Partner Section
  const WhyPartnerSection = () => (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Why Partner with CNESS?
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Unlock exclusive benefits and grow your business with the conscious enterprise movement
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: TrendingUp,
              title: "Revenue Growth",
              description: "Earn competitive commissions and expand your service offerings with new revenue streams",
              color: "from-green-500 to-emerald-600"
            },
            {
              icon: Network,
              title: "Network Access",
              description: "Connect with purpose-driven organizations and thought leaders worldwide",
              color: "from-blue-500 to-indigo-600"
            },
            {
              icon: Award,
              title: "Credibility Boost",
              description: "Enhance your reputation with official CNESS partnership credentials and badges",
              color: "from-purple-500 to-violet-600"
            },
            {
              icon: BookOpen,
              title: "Expert Resources",
              description: "Access exclusive training, tools, certifications, and marketing materials",
              color: "from-orange-500 to-red-600"
            },
            {
              icon: Headphones,
              title: "Dedicated Support",
              description: "Get priority assistance from our partner success team at every stage",
              color: "from-pink-500 to-rose-600"
            },
            {
              icon: Globe,
              title: "Global Reach",
              description: "Participate in a worldwide movement transforming how businesses operate",
              color: "from-cyan-500 to-blue-600"
            }
          ].map((benefit, i) => (
            <Card key={i} className="group border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
              <CardContent className="p-8">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${benefit.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );

  // Partner Types Section
  const PartnerTypesSection = () => {
    const partnerTypes = partnerData?.partner_types || {
      credibility: {
        name: "Credibility Partner",
        icon: "award",
        color: "#8B5CF6",
        description: "Enhance trust through co-branding and ecosystem recognition",
        ideal_for: "Industry associations, certification bodies, thought leaders",
        benefits: ["Co-branding opportunities", "Ecosystem recognition", "Joint marketing", "Priority events", "Exclusive badge"]
      },
      client_network: {
        name: "Client Network Partner",
        icon: "users",
        color: "#3B82F6",
        description: "Professionals who introduce CNESS to their existing clients",
        ideal_for: "Accountants, legal advisors, business consultants",
        benefits: ["Referral commissions", "Client value-add", "Co-branded materials", "Priority support", "Business reviews"]
      },
      advisor: {
        name: "Advisor Partner",
        icon: "lightbulb",
        color: "#F59E0B",
        description: "Consultants guiding organizations through assessments",
        ideal_for: "Management consultants, ESG specialists, coaches",
        benefits: ["Advisory fees", "Advanced training", "Client success tools", "Certifications", "Expert directory"]
      },
      implementation: {
        name: "Implementation Partner",
        icon: "settings",
        color: "#10B981",
        description: "Operators assisting with documentation and workshops",
        ideal_for: "Implementation consultants, project managers",
        benefits: ["Implementation fees", "Project tools", "Template library", "Tech support", "Success features"]
      },
      referral: {
        name: "Referral Partner",
        icon: "share-2",
        color: "#EC4899",
        description: "Simple referral program with easy tracking",
        ideal_for: "Influencers, networkers, industry contacts",
        benefits: ["Simple rewards", "Easy tracking", "Marketing materials", "Referral links", "Monthly payouts"]
      },
      channel: {
        name: "Channel Partner",
        icon: "trending-up",
        color: "#EF4444",
        description: "Firms actively selling and co-selling CNESS",
        ideal_for: "Resellers, VARs, system integrators",
        benefits: ["Highest commissions", "Deal protection", "Sales enablement", "Lead sharing", "Partner manager"]
      }
    };

    return (
      <section id="partner-types" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Find Your Perfect Partnership
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Six distinct partner types designed to match your expertise and business model
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Object.entries(partnerTypes).map(([key, type]) => {
              const IconComponent = PARTNER_TYPE_ICONS[key] || Award;
              return (
                <Card 
                  key={key}
                  className={`group cursor-pointer border-2 transition-all duration-300 hover:shadow-2xl overflow-hidden ${
                    selectedPartnerType === key ? 'border-purple-500 shadow-xl' : 'border-gray-100 hover:border-gray-200'
                  }`}
                  onClick={() => setSelectedPartnerType(selectedPartnerType === key ? null : key)}
                  data-testid={`partner-type-${key}`}
                >
                  <CardContent className="p-8">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${type.color}20` }}
                    >
                      <IconComponent className="w-8 h-8" style={{ color: type.color }} />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{type.name}</h3>
                    <p className="text-gray-600 mb-4">{type.description}</p>
                    
                    <div className="mb-4">
                      <span className="text-sm font-semibold text-gray-500">Ideal for:</span>
                      <p className="text-sm text-gray-700">{type.ideal_for}</p>
                    </div>
                    
                    {selectedPartnerType === key && (
                      <div className="mt-6 pt-6 border-t border-gray-100 animate-in fade-in duration-300">
                        <span className="text-sm font-semibold text-gray-900 mb-3 block">Key Benefits:</span>
                        <ul className="space-y-2">
                          {type.benefits?.map((benefit, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                              {benefit}
                            </li>
                          ))}
                        </ul>
                        <Button 
                          className="w-full mt-6"
                          style={{ backgroundColor: type.color }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData(prev => ({ ...prev, partner_type: key }));
                            setShowApplicationForm(true);
                          }}
                        >
                          Apply as {type.name}
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>
    );
  };

  // Tiers Section
  const TiersSection = () => {
    const tiers = partnerData?.tiers || {
      registered: {
        name: "Registered",
        color: "#6B7280",
        description: "Entry tier with basic access",
        benefits: ["Basic portal access", "Starter content", "Referral capability", "Basic badge", "Email support"]
      },
      authorized: {
        name: "Authorized",
        color: "#3B82F6",
        description: "Verified partners with expanded access",
        benefits: ["Full portal access", "Advanced training", "Deal registration", "+20% commissions", "Directory listing", "Priority support"]
      },
      strategic: {
        name: "Strategic",
        color: "#F59E0B",
        description: "Premium tier with maximum benefits",
        benefits: ["Premium features", "Co-marketing rights", "Priority leads", "Advanced analytics", "+50% commissions", "Dedicated manager", "Executive sponsorship"]
      }
    };

    return (
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Partner Tiers
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Progress through tiers and unlock greater benefits as you grow
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {Object.entries(tiers).map(([key, tier], index) => {
              const IconComponent = TIER_ICONS[key] || CheckCircle;
              const isMiddle = index === 1;
              
              return (
                <Card 
                  key={key}
                  className={`relative overflow-hidden border-0 ${
                    isMiddle ? 'md:-mt-4 md:mb-4' : ''
                  }`}
                  style={{ 
                    backgroundColor: key === 'strategic' ? '#1e1b4b' : '#1f2937',
                    border: `2px solid ${tier.color}40`
                  }}
                >
                  {key === 'strategic' && (
                    <div className="absolute top-0 right-0 bg-gradient-to-l from-yellow-400 to-orange-500 text-black text-xs font-bold px-4 py-1 rounded-bl-lg">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <CardContent className="p-8">
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center mb-6"
                      style={{ backgroundColor: `${tier.color}20` }}
                    >
                      <IconComponent className="w-7 h-7" style={{ color: tier.color }} />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-2">{tier.name}</h3>
                    <p className="text-white/60 mb-6">{tier.description}</p>
                    
                    <ul className="space-y-3">
                      {tier.benefits?.map((benefit, i) => (
                        <li key={i} className="flex items-center gap-3 text-white/80">
                          <Check className="w-5 h-5 flex-shrink-0" style={{ color: tier.color }} />
                          <span className="text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-white/60 mb-6">
              All partners start at Registered tier and can progress based on performance
            </p>
            <Button 
              onClick={() => setShowApplicationForm(true)}
              className="px-8 py-6 text-lg bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-semibold rounded-full"
            >
              Start Your Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
    );
  };

  // FAQ Section
  const FaqSection = () => (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-6 max-w-3xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
        </div>
        
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <Card 
              key={i}
              className={`border cursor-pointer transition-all duration-300 ${
                expandedFaq === i ? 'border-purple-200 shadow-lg' : 'border-gray-100 hover:border-gray-200'
              }`}
              onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  {expandedFaq === i ? (
                    <ChevronUp className="w-5 h-5 text-purple-500 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </div>
                {expandedFaq === i && (
                  <p className="mt-4 text-gray-600 leading-relaxed animate-in fade-in duration-300">
                    {faq.answer}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );

  // Application Form Modal
  const ApplicationFormModal = () => (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowApplicationForm(false)} />
      
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <Card className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl">
          <button 
            onClick={() => setShowApplicationForm(false)}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
          
          <CardHeader className="sticky top-0 bg-white border-b z-10 rounded-t-2xl">
            <CardTitle className="text-2xl font-bold text-gray-900">
              Partner Application
            </CardTitle>
            <p className="text-gray-600">Join the CNESS Global Partner Program</p>
          </CardHeader>
          
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Applicant Type */}
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Applicant Type *</Label>
                <div className="grid grid-cols-2 gap-4">
                  {['organization', 'individual'].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, applicant_type: type }))}
                      className={`p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                        formData.applicant_type === type 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {type === 'organization' ? <Building2 className="w-5 h-5" /> : <User className="w-5 h-5" />}
                      <span className="font-medium capitalize">{type}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Organization Details */}
              {formData.applicant_type === 'organization' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Organization Name *</Label>
                    <Input 
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Legal Name</Label>
                    <Input 
                      name="organization_legal_name"
                      value={formData.organization_legal_name}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
              
              {/* Contact Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-500" />
                  Contact Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>First Name *</Label>
                    <Input 
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Last Name *</Label>
                    <Input 
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input 
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input 
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Job Title</Label>
                    <Input 
                      name="job_title"
                      value={formData.job_title}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Location */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-purple-500" />
                  Location
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Country *</Label>
                    <Input 
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>City *</Label>
                    <Input 
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Online Presence */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-500" />
                  Online Presence
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Website</Label>
                    <Input 
                      name="website"
                      type="url"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>LinkedIn URL</Label>
                    <Input 
                      name="linkedin_url"
                      type="url"
                      value={formData.linkedin_url}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Partner Type */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-purple-500" />
                  Partnership Details
                </h3>
                <div>
                  <Label>Partner Type *</Label>
                  <select
                    name="partner_type"
                    value={formData.partner_type}
                    onChange={handleInputChange}
                    required
                    className="w-full mt-1 p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Select Partner Type</option>
                    <option value="credibility">Credibility Partner</option>
                    <option value="client_network">Client Network Partner</option>
                    <option value="advisor">Advisor Partner</option>
                    <option value="implementation">Implementation Partner</option>
                    <option value="referral">Referral Partner</option>
                    <option value="channel">Channel Partner</option>
                  </select>
                </div>
                
                {/* Industries */}
                <div>
                  <Label>Industries Served *</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {industries.map(industry => (
                      <button
                        key={industry}
                        type="button"
                        onClick={() => handleMultiSelect('industries_served', industry)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          formData.industries_served.includes(industry)
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {industry}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Geographic Coverage */}
                <div>
                  <Label>Geographic Coverage *</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {regions.map(region => (
                      <button
                        key={region}
                        type="button"
                        onClick={() => handleMultiSelect('geographic_coverage', region)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          formData.geographic_coverage.includes(region)
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {region}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Services */}
                <div>
                  <Label>Services Offered</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {services.map(service => (
                      <button
                        key={service}
                        type="button"
                        onClick={() => handleMultiSelect('services_offered', service)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          formData.services_offered.includes(service)
                            ? 'bg-purple-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {service}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Motivation */}
              <div>
                <Label>Why do you want to partner with CNESS? *</Label>
                <Textarea 
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  placeholder="Tell us about your goals and how you see this partnership benefiting both parties..."
                  className="mt-1"
                />
              </div>
              
              {/* Consents */}
              <div className="space-y-3 pt-4 border-t">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    name="terms_accepted"
                    checked={formData.terms_accepted}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-600">
                    I accept the <a href="#" className="text-purple-600 hover:underline">Partner Terms and Conditions</a> *
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    name="privacy_accepted"
                    checked={formData.privacy_accepted}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-600">
                    I have read and agree to the <a href="#" className="text-purple-600 hover:underline">Privacy Policy</a> *
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input 
                    type="checkbox"
                    name="marketing_opt_in"
                    checked={formData.marketing_opt_in}
                    onChange={handleInputChange}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-600">
                    I'd like to receive partner updates and marketing communications
                  </span>
                </label>
              </div>
              
              {submitError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {submitError}
                </div>
              )}
              
              {/* Submit */}
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full py-6 text-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-xl"
                data-testid="submit-application-btn"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Success Modal
  const SuccessModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSubmitSuccess(false)} />
      <Card className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
          <p className="text-gray-600 mb-6">
            Thank you for applying to the CNESS Partner Program. Our team will review your application and get back to you within 2-3 business days.
          </p>
          <Button 
            onClick={() => setSubmitSuccess(false)}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
          >
            Got it!
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  // CTA Section
  const CtaSection = () => (
    <section className="py-24 bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700">
      <div className="container mx-auto px-6 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Ready to Transform Your Business?
        </h2>
        <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
          Join hundreds of partners already growing with CNESS. Start your partnership journey today.
        </p>
        <Button 
          onClick={() => setShowApplicationForm(true)}
          className="px-10 py-6 text-lg bg-white text-purple-700 hover:bg-gray-100 font-semibold rounded-full shadow-2xl transition-all duration-300 hover:scale-105"
        >
          Apply Now
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        <HeroSection />
        <WhyPartnerSection />
        <PartnerTypesSection />
        <TiersSection />
        <FaqSection />
        <CtaSection />
      </main>
      
      <Footer />
      
      {showApplicationForm && <ApplicationFormModal />}
      {submitSuccess && <SuccessModal />}
    </div>
  );
};

export default PartnerProgramPage;
